import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import useSEO from '../../lib/useSEO';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogPost() {
  const { slug } = useParams();
  const isMobile = useIsMobile();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`)
      .then(res => setPost(res.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO(post ? {
    title: (post.metaTitle || post.title) + ' | ClearSeg Blog',
    description: post.metaDescription || post.excerpt || '',
    canonical: 'https://clearseg.com/blog/' + slug,
  } : {});

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh' }}>
        <MarketingNav />
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <p style={{ color: C.muted, fontSize: 16 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh' }}>
        <MarketingNav />
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <h1 style={{ color: C.navy, fontSize: 32, marginBottom: 16 }}>Post Not Found</h1>
          <Link to="/blog" style={{ color: C.accent, fontSize: 16 }}>&larr; Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <MarketingNav />

      <article style={{
        maxWidth: 720, margin: '0 auto',
        padding: isMobile ? '32px 20px 48px' : '60px 24px 80px',
      }}>
        <Link to="/blog" style={{
          color: C.muted, fontSize: 14, textDecoration: 'none', display: 'inline-block', marginBottom: 32,
        }}>
          &larr; Back to Blog
        </Link>

        <h1 style={{
          color: C.navy, fontSize: isMobile ? 28 : 40, fontWeight: 800,
          letterSpacing: -1, lineHeight: 1.15, margin: '0 0 16px',
        }}>
          {post.title}
        </h1>

        <div style={{ display: 'flex', gap: 16, marginBottom: 40, fontSize: 14, color: C.muted }}>
          <span>{formatDate(post.publishedAt || post.createdAt)}</span>
          {post.readTime && <span>{post.readTime}</span>}
        </div>

        <style>{`
          .blog-content h2 { color: ${C.navy}; font-size: ${isMobile ? '22px' : '26px'}; font-weight: 700; margin: 40px 0 16px; line-height: 1.3; }
          .blog-content h3 { color: ${C.navy}; font-size: ${isMobile ? '18px' : '20px'}; font-weight: 700; margin: 32px 0 12px; line-height: 1.3; }
          .blog-content p { color: ${C.muted}; font-size: ${isMobile ? '16px' : '18px'}; line-height: 1.8; margin: 0 0 20px; }
          .blog-content strong { color: ${C.navy}; }
          .blog-content ul, .blog-content ol { color: ${C.muted}; font-size: ${isMobile ? '16px' : '18px'}; line-height: 2; margin: 0 0 24px; padding-left: 24px; }
          .blog-content li { margin-bottom: 4px; }
          .blog-content a { color: ${C.accent}; text-decoration: none; font-weight: 600; }
          .blog-content a:hover { text-decoration: underline; }
        `}</style>
        <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

        <div style={{
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: isMobile ? 28 : 40, textAlign: 'center', marginTop: 48,
        }}>
          <h3 style={{ color: C.navy, fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>
            Ready to see how much you can save?
          </h3>
          <p style={{ color: C.muted, fontSize: 15, margin: '0 0 24px', lineHeight: 1.6 }}>
            Use our free calculator to estimate your savings in under 60 seconds.
          </p>
          <Link to="/calculator" style={{ textDecoration: 'none' }}>
            <button style={{
              background: C.navy, color: '#FFFFFF', border: 'none',
              padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 700,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => { e.target.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.target.style.opacity = '1'; }}
            >
              Try the Savings Calculator
            </button>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}
